import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface ApplicationCompletion {
  isCompleteProfile: boolean;
  isCompleteWorksetup: boolean;
  isStripeSetup: boolean;
  isCompleteCleaningSuppplies: boolean;
  isCompleteAvailabilitySetup: boolean;
  currentStep: number;
}

/**
 * Default state object
 * Keeps things predictable and avoids null checks everywhere
 */
const defaultApplicationCompletion: ApplicationCompletion = {
  isCompleteProfile: false,
  isCompleteWorksetup: false,
  isStripeSetup: false,
  isCompleteCleaningSuppplies: false,
  isCompleteAvailabilitySetup: false,
  currentStep: 2, // step 1 is awlays completed
};

type UserOnboardingState = {
  applicationCompletion: ApplicationCompletion;

  setApplicationCompletion: (data: ApplicationCompletion) => void;
  resetApplicationCompletion: () => void;

  toggleCleaningSuppliesCompletion: () => void;
  toggleProfileCompletion: () => void;
  toggleWorksetupCompletion: () => void;
  toggleStripeSetup: () => void;
  toggleAvailabilitySetup: () => void;

  setCurrentStep: (step: number) => void;
};

export const userOnboardingCompletionStore = create<UserOnboardingState>()(
  devtools(
    set => ({
      applicationCompletion: defaultApplicationCompletion,

      setApplicationCompletion: applicationCompletion =>
        set({ applicationCompletion }),

      resetApplicationCompletion: () =>
        set({ applicationCompletion: defaultApplicationCompletion }),

      toggleCleaningSuppliesCompletion: () =>
        set(state => ({
          applicationCompletion: {
            ...state.applicationCompletion,
            isCompleteCleaningSuppplies:
              !state.applicationCompletion.isCompleteCleaningSuppplies,
          },
        })),

      toggleProfileCompletion: () =>
        set(state => ({
          applicationCompletion: {
            ...state.applicationCompletion,
            isCompleteProfile: true,
            isCompleteWorksetup: true,
            isCompleteCleaningSuppplies: true,
            currentStep: 3, // move from step 2 to step 3
          },
        })),

      toggleWorksetupCompletion: () =>
        set(state => ({
          applicationCompletion: {
            ...state.applicationCompletion,
            isCompleteWorksetup:
              !state.applicationCompletion.isCompleteWorksetup,
          },
        })),

      toggleStripeSetup: () =>
        set(state => ({
          applicationCompletion: {
            ...state.applicationCompletion,
            isStripeSetup: !state.applicationCompletion.isStripeSetup,
          },
        })),

      toggleAvailabilitySetup: () =>
        set(state => ({
          applicationCompletion: {
            ...state.applicationCompletion,
            isCompleteAvailabilitySetup:
              !state.applicationCompletion.isCompleteAvailabilitySetup,
          },
        })),

      setCurrentStep: (step: number) =>
        set(state => ({
          applicationCompletion: {
            ...state.applicationCompletion,
            currentStep: step,
          },
        })),
    }),
    { name: 'onboardingCompletionStore' }
  )
);
